import Swiper from 'swiper'
import SwiperCore, { Mousewheel, Navigation, Thumbs, Zoom } from 'swiper/core'
import { throttle } from '~/helpers/general'
import classes from '~/config/classes'

SwiperCore.use([Navigation])
SwiperCore.use([Thumbs])
SwiperCore.use([Mousewheel])
SwiperCore.use([Zoom])

export default class ProductThumbnailsSlider {
  constructor(component) {
    this.dom = {
      $component: component,
      $thumbnailSlider: component.querySelector('.js-thumbnailsSlider'),
      $thumbnailSlide: component.querySelectorAll('[js-thumbnails-slider="thumbnail"]'),
      $featuredSlider: component.querySelector('.js-featuredSlider'),
      $zoomContainer: component.querySelector('.js-zoom-container'),
      $zoomIcons: component.querySelectorAll('.js-zoom-icon'),
      $nextThumbs: component.querySelector('[js-thumbnails-slider="nextThumbs"]'),
      $prevThumbs: component.querySelector('[js-thumbnails-slider="prevThumbs"]'),
      $featuredSlide: '.js-galleryFeatured',
      $container: document.querySelector('.js-productPageContainer'),
    }
    this.state = {
      imageLoadedClass: 'imageLoaded',
      zoomReadyClass: 'zoom-ready',
      zoomedInClass: 'zoomed-in',
      scrollLockClass: 'scroll-locked',
      slideEdge: true,
      slideEnd: false,
      scrolling: false,
    }
    this.mount()
  }

  mount() {

    /*
      Timeout is being used because swiper sometimes fails to
      calculate the slide heights properly
    */
    setTimeout(() => {
      this.initSlider()
    }, 100)

    this.addEventListeners()
  }

  initSlider() {
    const { dom } = this
    const initialSlide = dom.$container.dataset.activeSlide || 0

    if (dom.$container.dataset.activeSlide &&
    dom.$thumbnailSlide.length < dom.$container.dataset.activeSlide) {
      dom.$container.dataset.activeSlide = dom.$thumbnailSlide.length - 1
    }

    const thumbnail = new Swiper(dom.$thumbnailSlider, {
      direction: 'vertical',
      slidesPerView: dom.$thumbnailSlide.length >= 5 ? 5 : dom.$thumbnailSlide.length,
      autoHeight: false,
      watchSlidesProgress: true,
    })

    const zoomLevel = window.theme.strings.productPage?.zoomLevel || 2
    const swiper = new Swiper(this.dom.$featuredSlider, {
      direction: 'horizontal',
      slidesPerView: 1,
      autoHeight: false,
      initialSlide,
      mousewheel: {
        forceToAxis: true,
        sensitivity: 10,
        releaseOnEdges: true,
      },
      zoom: {
        maxRatio: zoomLevel,
        toggle: false,
      },
      navigation: {
        nextEl: dom.$nextThumbs,
        prevEl: dom.$prevThumbs,
      },
      speed: 600,
      thumbs: {
        swiper: thumbnail,
      },
      breakpoints: {
        768: {
          direction: 'vertical',
          mousewheel: {
            forceToAxis: true,
            sensitivity: 10,
            releaseOnEdges: true,
          },
        },
      },
    })

    swiper.on('activeIndexChange', (swiperEl) => {
      dom.$container.dataset.activeSlide = swiperEl.activeIndex
    })

    swiper.once('lazyImageReady', this.loadImages(swiper))
    swiper.on('init', this.handleSwiperReady(swiper))
    swiper.on('resize', this.getZoomContainerWidth())
    swiper.on('zoomChange', (swiperEl, scale) => this.updateZoomState(swiperEl, scale))

    swiper.on('slideChangeTransitionStart', (swiperEl) => {
      swiperEl.$el[0].classList.add('transitioning')
    })

    swiper.on('slideChangeTransitionEnd', (swiperEl) => {
      swiperEl.$el[0].classList.remove('transitioning')
    })

    swiper.on('click', (swiperEl) => {
      if (swiperEl.zoom.currentScale > 1) {
        swiperEl.zoom.out()
      } else {
        swiperEl.zoom.in()
      }
    })

    swiper.on('scroll', (swiperEl, event) => throttle(this.handleScrollLock(swiperEl, event), 300))
  }

  /**
   *
   * @param {Object} swiperEl - swiper object
   * - there's some delay added in the logic to prevent sudden changes in
   * wheel direction when arriving to slide edge
   */
  handleScrollLock(swiperEl) {
    if (window.matchMedia('(max-width: 767px)').matches) {
      return
    }

    const { state } = this
    const bodyHasScrollLockedClass = document.body.classList.contains(state.scrollLockClass)
    state.slideEdge = swiperEl.isBeginning || swiperEl.isEnd
    state.slideEnd = swiperEl.isEnd
    window.basis.EventBus.emit('swiper-slide-edge', state.slideEdge)
    window.basis.EventBus.emit('swiper-slide-scrolling', true)

    // When active slide reaches the beginning or end
    if (swiperEl.isBeginning) {
      if (bodyHasScrollLockedClass) {
        setTimeout(() => {
          window.basis.EventBus.emit('swiper-enable-body-scroll')
          window.basis.EventBus.emit('swiper-slide-scrolling', false)
        }, 600)
      }
      return
    }

    if (swiperEl.isEnd) {
      setTimeout(() => {
        window.basis.EventBus.emit('swiper-enable-body-scroll')
        window.basis.EventBus.emit('swiper-slide-scrolling', false)
      }, 800)
      return
    }

    // When active slide is in the middle
    if (!bodyHasScrollLockedClass) {
      if (swiperEl.previousIndex < swiperEl.activeIndex) {
        window.basis.EventBus.emit('swiper-disable-body-scroll')
      }
    } else if (bodyHasScrollLockedClass) {
      if (swiperEl.previousIndex > swiperEl.activeIndex) {
        window.basis.EventBus.emit('swiper-enable-body-scroll')
      }
    }

    setTimeout(() => {
      window.basis.EventBus.emit('swiper-slide-scrolling', false)
    }, 100)
  }

  updateZoomState(swiperEl, scale) {
    const { dom, state } = this

    if (scale === 1) {
      dom.$featuredSlider.classList.remove(state.zoomedInClass)
    } else {
      dom.$featuredSlider.classList.add(state.zoomedInClass)
    }
  }

  handleSwiperReady(swiper) {
    const { dom } = this
    this.getZoomContainerWidth(swiper)
    this.setEventListeners(swiper)
    dom.$component.classList.add(classes.active)
    window.basis.EventBus.emit('swiper-ready')
  }

  setEventListeners(swiper) {
    const { dom } = this

    dom.$zoomIcons.forEach((icon) => {
      icon.addEventListener('click', () => {
        if (swiper.zoom.currentScale > 1) {
          swiper.zoom.out()
        } else {
          swiper.zoom.in()
        }
      })
    })

    window.basis.EventBus.listen('swiper-destroy', () => {
      swiper.destroy()
    })
  }

  getZoomContainerWidth() {
    setTimeout(() => {
      const { dom } = this
      const width = dom.$zoomContainer.offsetWidth
      document.documentElement.style.setProperty('--zoom-container-width', `${width}px`)
      dom.$featuredSlider.classList.add('zoom-ready')
    }, 100)
  }

  loadImages(swiper) {
    const { state } = this

    swiper.slides.forEach((item) => {
      const image = item.querySelector('.js-productImage')
      const imageSrc = image?.dataset.src

      if (imageSrc) {
        const img = new Image()
        img.src = imageSrc

        img.addEventListener('load', () => {
          image.src = imageSrc
          item.classList.add(state.imageLoadedClass)
        })
      }
    })

  }

  addEventListeners() {
    window.addEventListener('resize', throttle(this.getZoomContainerWidth.bind(this), 300))
  }
}
